import { debounce, isEmpty, uniqBy } from 'lodash-es';
import {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useClientLogContext } from 'src/contexts/ClientLogContext';
import { useSiteTheme } from 'src/contexts/SiteTheme/SiteThemeContext';
import { vars } from 'src/core/themes';
import { useMatchMedia } from 'src/hooks/useMatchMedia';
import { FocalPointInfo, SectionInfo } from 'src/WebApiController';
import svgPanZoom from 'svg-pan-zoom';

import { addFocalPoint } from '../shared/utils/addFocalPoint';
import {
  addSectionAnimation,
  crossesPoints,
  DrawingPoint,
  isMissiongSectionData,
  isSvgValid,
  registerDrawLine,
  registerPanningListners,
  registerTapAndHold,
  svgPanZoomMobileHandler,
  tryCleanAnimation,
} from '../VenueMapContent.hooks';
import { StandardColorSpec } from '../VenueMapProps';
import {
  GetColor,
  getSectionId,
  initializeGroups,
  resetSectionRowColors,
} from './VenueMapV3Content.utils';

const clickEvents = ['click'];

const ZOOM_SCALE_SENSITIVITY = 0.2;

type SvgMapInitializerProps = {
  availableSections: SectionInfo[];
  selectedSections?: SectionInfo[];
  markedSectionIds?: number[];
  animatedSectionIds?: number[];
  onSectionClicked?: (
    e: MouseEvent,
    selectedSection: SectionInfo,
    surroundingSections?: SectionInfo[]
  ) => void;
  onSectionTapAndHold?: (
    e: MouseEvent | TouchEvent,
    selectedSection: SectionInfo
  ) => void;
  onSectionHovered?: (hoveredSection: SectionInfo) => ReactNode;
  setSelectedSections?: (
    sections: SectionInfo[],
    newSectionIds?: number[]
  ) => void;
  isZoomEnabled: boolean;
  isPanEnabled?: boolean;
  showDefaultMapColors?: boolean;
  focalPoint?: FocalPointInfo | null;
  colors?: StandardColorSpec;
  getColor?: GetColor;
  isHeatMap?: boolean;
  useRowMap?: boolean;
  disableHoverEffect?: boolean;
  allowDrawLine?: boolean;
};

export const useSvgMap = ({
  availableSections,
  selectedSections,
  markedSectionIds,
  animatedSectionIds,
  onSectionClicked,
  onSectionTapAndHold,
  onSectionHovered,
  setSelectedSections,
  isZoomEnabled,
  isPanEnabled,
  focalPoint,
  getColor,
  showDefaultMapColors,
  isHeatMap = false,
  useRowMap = false,
  disableHoverEffect = false,
  allowDrawLine = true,
}: SvgMapInitializerProps) => {
  const { isDarkMode } = useSiteTheme();
  const { showErrorDialog } = useClientLogContext();
  const isMobile = useMatchMedia('mobile');
  const localRef = useRef<HTMLDivElement>();
  const isPanning = useRef(false);
  const onRemoveEventListenersRef = useRef<() => void>(() => void 0);
  const onDoulbeClickDoneRef = useRef<(points: DrawingPoint[]) => void>(
    () => void 0
  );

  const [hoveredContent, setHoverContent] = useState<ReactNode>();
  const [zoomFlag, setZoomFlag] = useState(false);
  const [venueMapSvg, setVenueMapSvg] = useState<SvgPanZoom.Instance>();
  const [sectionGroups, setSectionGroups] = useState<SVGElement[]>([]);

  const sectionIdsMissingRowOrder = useMemo(
    () => availableSections.filter(isMissiongSectionData).map(({ id }) => id),
    [availableSections]
  );

  useEffect(() => {
    if (!venueMapSvg) return;

    const deregisterDrawLine = registerDrawLine(
      localRef,
      onRemoveEventListenersRef,
      onDoulbeClickDoneRef,
      allowDrawLine,
      isDarkMode
    );
    return () => {
      deregisterDrawLine();
    };
  }, [allowDrawLine, isDarkMode, venueMapSvg]);

  const setSelectedHandler = useCallback(
    (e: Event) => {
      if (isPanning.current || e == null) {
        return;
      }

      const mouseEvent = e as MouseEvent;
      const target = e.target as SVGElement;

      const selectedSectionId = parseInt(getSectionId(target) ?? '0');
      const selectedSection = availableSections.find(
        (x) => x.id === selectedSectionId
      );

      selectedSection && onSectionClicked?.(mouseEvent, selectedSection);
    },
    [availableSections, onSectionClicked]
  );

  const tapAndHoldSectionHandler = useCallback(
    (e: Event) => {
      if (isPanning.current || !onSectionTapAndHold || e == null) {
        return undefined;
      }

      const mouseEvent = e as MouseEvent | TouchEvent;
      const target = e.target as SVGElement;

      const selectedSectionId = parseInt(getSectionId(target) ?? '0');
      const selectedSection = availableSections.find(
        (x) => x.id === selectedSectionId
      );

      selectedSection && onSectionTapAndHold(mouseEvent, selectedSection);
    },
    [availableSections, onSectionTapAndHold]
  );

  const mouseEnter = useCallback(
    ({ target }: MouseEvent) => {
      if (disableHoverEffect) return;
      const section = target as SVGElement;
      const sectionId = parseInt(getSectionId(section) ?? '0');

      const hoveredSection = availableSections.find((s) => s.id === sectionId);
      if (!hoveredSection) return;

      const hoveredContent = onSectionHovered?.(hoveredSection);
      setHoverContent(hoveredContent);
      const sectionPath = section.querySelector('path[t="s"]') as SVGElement;
      sectionPath?.setAttribute('fill-opacity', '0.75');
    },
    [availableSections, disableHoverEffect, onSectionHovered]
  );

  const mouseLeave = useCallback(
    ({ target }: MouseEvent) => {
      if (disableHoverEffect) return;
      const section = target as SVGElement;
      const sectionPath = section.querySelector('path[t="s"]') as SVGElement;

      // If there are row paths, set the section path to be transparent
      // otherwise set the section path to be opaque
      const sectionRows = [
        ...section.querySelectorAll('g[id="row"] path'),
      ] as HTMLElement[];
      if (!isEmpty(sectionRows)) {
        sectionPath?.setAttribute('fill-opacity', '0');
      } else {
        sectionPath?.setAttribute('fill-opacity', '1');
      }
    },
    [disableHoverEffect]
  );

  const initializeSvg = useCallback(
    (svgElement: SVGSVGElement): SvgPanZoom.Instance | undefined => {
      // Validate SVG dimensions before initialization
      if (!isSvgValid(svgElement)) {
        const rect = svgElement.getBoundingClientRect();
        const errorDimensions = { width: rect.width, height: rect.height };
        showErrorDialog(
          'VenueMapSvgV3.InvalidDimensions',
          {
            message: 'Venue Map SVG has invalid dimensions',
          },
          {
            customerHeader: 'Invalid Venue Map',
            trackErrorData: errorDimensions,
          }
        );
        return undefined;
      }

      addFocalPoint(isDarkMode, svgElement, focalPoint);

      try {
        return svgPanZoom(svgElement, {
          zoomScaleSensitivity: ZOOM_SCALE_SENSITIVITY,
          zoomEnabled: isZoomEnabled,
          panEnabled: isPanEnabled,
          dblClickZoomEnabled: false,
          onZoom: () => setZoomFlag(true),
          onPan: () => setZoomFlag(true),
          customEventsHandler: isMobile ? svgPanZoomMobileHandler : undefined,
        });
      } catch (error: unknown) {
        // Catch SVGMatrix errors during initialization
        showErrorDialog(
          'VenueMapSvgV3.FailedToInitialize',
          {
            message:
              `Failed to initialize venue map for pan and zoom: ` +
              (error as Error)?.message,
          },
          {
            customerHeader: 'Invalid Venue Map',
            trackErrorData: error as Error,
          }
        );
        return undefined;
      }
    },
    [
      focalPoint,
      isDarkMode,
      isMobile,
      isPanEnabled,
      isZoomEnabled,
      showErrorDialog,
    ]
  );

  const onResize = useCallback(() => {
    if (!localRef.current || !venueMapSvg) return;

    try {
      venueMapSvg.resize(); // update SVG cached size and controls positions
      venueMapSvg.fit();
      venueMapSvg.center();
    } catch (error: unknown) {
      // Catch SVGMatrix errors (non-invertible matrix or non-finite values) during resize
      showErrorDialog('VenueMapSvgV3.FailedToResize', {
        message: `Failed to resize Venue Map: ` + (error as Error)?.message,
      });
    }
  }, [showErrorDialog, venueMapSvg]);

  useEffect(() => {
    if (!venueMapSvg) return;

    const debounceOnResize = debounce(onResize, 200);

    // add eventListeners
    const onAddingEventListeners = () => {
      sectionGroups.forEach((element) => {
        const sectionId = parseInt(getSectionId(element) ?? '0');
        const isSelected =
          selectedSections?.some(({ id }) => id === sectionId) ?? false;
        const isMarked = markedSectionIds?.some((id) => id === sectionId);
        const shouldAnimate = animatedSectionIds?.some(
          (id) => id === sectionId
        );

        const sectionPath = element.querySelector('path[t="s"]') as SVGElement;
        const sectionLabel = element.querySelector('text[t="s"]') as SVGElement;

        element.style.cursor = 'pointer';
        sectionPath && tryCleanAnimation(sectionPath);

        const sectionRows = [
          ...element.querySelectorAll('g[id="row"] path'),
        ] as HTMLElement[];
        // If there are row paths, set the section path to be transparent
        // otherwise set the section path to be opaque
        if (!isEmpty(sectionRows)) {
          sectionPath?.setAttribute(
            'fill',
            vars.color.backgroundHighlightHover
          );
          sectionPath?.setAttribute('fill-opacity', '0');
        } else {
          sectionPath?.setAttribute('fill-opacity', '1');
        }
        sectionPath?.setAttribute('stroke-width', '2px');
        sectionPath?.setAttribute('stroke', 'black');
        sectionLabel?.removeAttribute('fill');

        if (isSelected) {
          sectionPath?.setAttribute('stroke', vars.color.textBrand);
          sectionPath?.setAttribute('stroke-width', '6px');
          sectionLabel?.setAttribute('fill', vars.color.textBrand);
        } else if (isMarked) {
          sectionPath?.setAttribute('stroke-width', '6px');
          if (shouldAnimate && sectionPath) {
            addSectionAnimation(sectionPath);
            sectionLabel?.setAttribute('fill', vars.color.textBrand);
          } else {
            sectionPath?.setAttribute('stroke', vars.color.textBrand);
          }
        }

        element.addEventListener('mouseenter', mouseEnter);
        element.addEventListener('mouseleave', mouseLeave);
        clickEvents.forEach((e) =>
          element.addEventListener(e, setSelectedHandler)
        );
      });

      window.addEventListener('resize', debounceOnResize);
    };

    // remove eventListeners
    const onRemoveEventListeners = () => {
      window.removeEventListener('resize', debounceOnResize);

      sectionGroups.forEach((element) => {
        element.removeEventListener('mouseenter', mouseEnter);
        element.removeEventListener('mouseleave', mouseLeave);
        clickEvents.forEach((e) =>
          element.removeEventListener(e, setSelectedHandler)
        );
      });
    };

    onRemoveEventListenersRef.current = onRemoveEventListeners;
    onDoulbeClickDoneRef.current = (points: DrawingPoint[]) => {
      onAddingEventListeners();
      const selectedIds = new Set<number>();
      sectionGroups.forEach((element) => {
        if (crossesPoints(points, element.getBoundingClientRect())) {
          selectedIds.add(parseInt(getSectionId(element) ?? '-1'));
        }
      });
      const updatedSections = availableSections
        .filter(
          ({ id }) => selectedIds.has(id) || markedSectionIds?.includes(id)
        )
        .toSorted((a, b) => a.name?.localeCompare(b.name));

      if (!isEmpty(updatedSections)) {
        setSelectedSections?.(
          uniqBy(updatedSections, 'id'),
          Array.from(selectedIds)
        );
      }
    };

    const deregisterPanEvents = registerPanningListners(
      localRef.current,
      isPanning
    );

    const deregisterTapAndHold = registerTapAndHold(
      sectionGroups,
      tapAndHoldSectionHandler,
      onSectionTapAndHold != null
    );

    onAddingEventListeners();

    return () => {
      onRemoveEventListeners();
      deregisterPanEvents();
      deregisterTapAndHold();
    };
  }, [
    availableSections,
    animatedSectionIds,
    markedSectionIds,
    mouseEnter,
    mouseLeave,
    onResize,
    sectionGroups,
    selectedSections,
    setSelectedHandler,
    setSelectedSections,
    venueMapSvg,
    tapAndHoldSectionHandler,
    onSectionTapAndHold,
  ]);

  const onInitializeContent = useCallback(
    (element: HTMLDivElement | null, reset = false) => {
      if (!element) return false;
      if (!reset && !!venueMapSvg) return false;
      localRef.current = element;
      const svgElement = element.getElementsByTagName('svg')[0];
      svgElement.style.transition = 'opacity 0.3s ease';
      svgElement.setAttribute('opacity', '0');
      // The initialize part will clean up the unused groups and set the row level colors
      const sectionGroups = initializeGroups(
        svgElement,
        availableSections,
        selectedSections,
        markedSectionIds,
        animatedSectionIds,
        getColor,
        showDefaultMapColors,
        sectionIdsMissingRowOrder,
        isHeatMap,
        useRowMap,
        isDarkMode
      );
      setSectionGroups(sectionGroups);
      setTimeout(() => {
        svgElement.setAttribute('opacity', '1');
      }, 50);

      // Initialize the SVG pan zoom with the cleaned up svgElement
      const svg = initializeSvg(svgElement);
      setVenueMapSvg(svg);
      return true;
    },
    [
      venueMapSvg,
      availableSections,
      selectedSections,
      markedSectionIds,
      animatedSectionIds,
      getColor,
      showDefaultMapColors,
      sectionIdsMissingRowOrder,
      isHeatMap,
      useRowMap,
      initializeSvg,
      isDarkMode,
    ]
  );

  // this is to refresh the row colors in regards to to selected sections changes
  useEffect(() => {
    if (localRef.current && venueMapSvg && !isEmpty(sectionGroups)) {
      resetSectionRowColors(
        sectionGroups,
        availableSections,
        selectedSections,
        markedSectionIds,
        animatedSectionIds,
        sectionIdsMissingRowOrder,
        getColor,
        isHeatMap,
        showDefaultMapColors
      );
    }
  }, [
    availableSections,
    animatedSectionIds,
    getColor,
    isHeatMap,
    markedSectionIds,
    sectionGroups,
    sectionIdsMissingRowOrder,
    selectedSections,
    showDefaultMapColors,
    venueMapSvg,
  ]);

  const onZoom = useCallback(
    (zoomIn = true) => {
      if (!venueMapSvg) return;
      zoomIn ? venueMapSvg.zoomIn() : venueMapSvg.zoomOut();
      setZoomFlag(true);
    },
    [venueMapSvg]
  );

  const onReset = useCallback(() => {
    onResize();
    setZoomFlag(false);
  }, [onResize]);

  return {
    onInitializeContent,
    onZoom,
    onReset,
    zoomFlag,
    hoveredContent,
  };
};
